import { Button, makeStyles } from '@fluentui/react-components';
import { Check, Download, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { IdentityWorkbench } from '../../hooks/useIdentityWorkbench';
import { RowVM, identityQueueToCsv } from './identityRowModel';

const useStyles = makeStyles({
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
  },
});

export interface IdentityQueueToolbarProps {
  wb: IdentityWorkbench;
}

/**
 * The queue's action cluster.
 *
 * It takes the workbench object rather than a dozen callbacks deliberately:
 * the selection-scoped actions, the campaign-wide sweep and the freeze shaping
 * are three views of the same state, and threading them individually is how the
 * two halves of the freeze drift apart.
 */
export default function IdentityQueueToolbar({ wb }: IdentityQueueToolbarProps) {
  const styles = useStyles();
  const {
    frozen,
    selectedCount,
    approvableSelected,
    removableSelected,
    acceptableRows,
    showAddPanel,
    setShowAddPanel,
    setSelectedKeys,
    handleApproveSelected,
    handleRemoveSelected,
    handleAcceptExactMatches,
    query,
    viewRows,
    staged,
    migrationId,
  } = wb;

  // FD-513 — exports the FILTERED, LOADED rows, and the filename says
  // `-loaded` so a spreadsheet that outlives this screen cannot be mistaken for
  // the campaign's full set. The device count comes from the same field the
  // Devices column renders, so the two cannot disagree; a non-suggestion row
  // has no device by construction, which is exactly why the suggestions feed
  // had nothing to hang it on.
  const exportCsv = () => {
    const csv = identityQueueToCsv(viewRows, staged, (vm: RowVM) =>
      vm.kind === 'suggestion' ? vm.row.deviceCount : 0,
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `identities-${migrationId}-loaded.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.headerActions}>
      {/* FD-383 — selection-scoped actions sit BEFORE the sweep and appear
          only while a selection exists, so the toolbar does not grow two
          permanently-disabled buttons. Counts are in the labels (the
          MigrationDevicesPage convention). */}
      {selectedCount > 0 && (
        <>
          <Button
            appearance="primary"
            icon={<Check size={16} strokeWidth={1.5} />}
            disabled={approvableSelected.length === 0}
            onClick={handleApproveSelected}
          >
            Approve {approvableSelected.length} selected
          </Button>
          {/* Withheld while the wave is active for the same reason the
              per-row Remove is: removal is a WHOLE-REQUEST 409, so a staged
              removal fails the save and takes every other staged change
              with it. */}
          {!frozen && (
            <Button
              appearance="secondary"
              icon={<Trash2 size={16} strokeWidth={1.5} />}
              disabled={removableSelected.length === 0}
              onClick={handleRemoveSelected}
            >
              Remove {removableSelected.length} mapping
              {removableSelected.length === 1 ? '' : 's'}
            </Button>
          )}
          <Button appearance="subtle" onClick={() => setSelectedKeys(new Set())}>
            Clear
          </Button>
        </>
      )}
      <Button
        appearance="primary"
        icon={<Check size={16} strokeWidth={1.5} />}
        disabled={acceptableRows.length === 0}
        onClick={handleAcceptExactMatches}
      >
        Accept {acceptableRows.length} exact match
        {acceptableRows.length === 1 ? '' : 'es'}
      </Button>
      {/* FD-381 — offered for D2C too. It used to be T2T-only because the
          flow began with a source-tenant search, which D2C has no directory
          for; the panel's D2C variant picks from observed device SIDs
          instead, so a domain-joined campaign now has a manual path. */}
      <Button
        appearance="secondary"
        icon={<Plus size={16} strokeWidth={1.5} />}
        onClick={() => setShowAddPanel((v) => !v)}
      >
        {showAddPanel ? 'Close add mapping' : 'Add mapping'}
      </Button>
      <Button
        appearance="secondary"
        icon={<Download size={16} strokeWidth={1.5} />}
        disabled={viewRows.length === 0}
        onClick={exportCsv}
      >
        Export {viewRows.length} loaded
      </Button>
      <Button
        appearance="subtle"
        icon={<RefreshCw size={16} strokeWidth={1.5} />}
        aria-label="Refresh identity suggestions"
        onClick={() => query.refetch()}
      />
    </div>
  );
}
